import React from 'react';
import { FormControlLabel, Switch, TextField } from '@mui/material';
import type { Location } from '../../api/config';

/**
 * Shared field group for the "Status & Monitoring" section of the Location
 * editor. Consumed by both `LocationForm` (modal) and `LocationDashboardPage`'s
 * inline editor — the field set, conventions (UTC override on the time helper
 * text), and gating (Auto-Send Time only shows when the toggle is on) live in
 * one place so the two editors can't drift.
 *
 * The caller provides outer chrome (section heading, container) and wires
 * `onChange` into its own state-update flow. `density='compact'` mirrors the
 * inline-edit pane on the dashboard; `density='standard'` (default) matches
 * the modal's full-size MUI controls.
 */
export type LocationStatusValue = Pick<
  Location,
  'active' | 'alerts_enabled' | 'auto_send_enabled' | 'digest_send_time' | 'timezone'
>;

interface StatusMonitoringFieldsProps {
  value: Partial<LocationStatusValue>;
  onChange: (patch: Partial<LocationStatusValue>) => void;
  density?: 'standard' | 'compact';
  /**
   * Optional override for the "Alerts Enabled" label. The modal uses this to
   * render a tooltip + inactive-location warning composition; the dashboard
   * gets the plain default.
   */
  alertsLabel?: React.ReactNode;
}

export const StatusMonitoringFields: React.FC<StatusMonitoringFieldsProps> = ({
  value,
  onChange,
  density = 'standard',
  alertsLabel,
}) => {
  const size = density === 'compact' ? 'small' : undefined;
  const switchSize = density === 'compact' ? 'small' : 'medium';

  return (
    <>
      <FormControlLabel
        control={
          <Switch
            size={switchSize}
            checked={!!value.active}
            onChange={e => onChange({ active: e.target.checked })}
          />
        }
        label="Active"
      />
      <FormControlLabel
        control={
          <Switch
            size={switchSize}
            checked={value.alerts_enabled !== false}
            onChange={e => onChange({ alerts_enabled: e.target.checked })}
          />
        }
        label={alertsLabel ?? 'Alerts Enabled'}
      />
      <FormControlLabel
        control={
          <Switch
            size={switchSize}
            checked={!!value.auto_send_enabled}
            onChange={e => onChange({ auto_send_enabled: e.target.checked })}
          />
        }
        label="Auto-Send"
      />
      {value.auto_send_enabled && (
        <TextField
          size={size}
          type="time"
          label="Auto-Send Time"
          value={value.digest_send_time ?? ''}
          onChange={e => onChange({ digest_send_time: e.target.value })}
          fullWidth={density === 'standard'}
          InputLabelProps={{ shrink: true }}
          inputProps={{ step: 60 }}
          helperText={
            value.timezone === 'UTC'
              ? `Location is on UTC, so the time is interpreted as Central Time (DST-aware). Defaults to 07:00 when blank.`
              : `Auto-send time in this location's timezone. Defaults to 07:00 when blank.`
          }
        />
      )}
    </>
  );
};
